import PropTypes from "prop-types";
import React from "react";

import { fireTrack } from "@swa-ui/analytics";
import { AriaLive, Dialog, Heading, useDialog } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import { CallToAction } from "../CallToAction";
import { HtmlValue } from "../HtmlValue";
import styles from "./RotatingBannerPlacement.module.scss";

export const RotatingBannerPlacement = (props) => {
  const { placement, placementData = {} } = props;
  const { callToAction, dialog, graphic, text } = placement ?? {};
  const { dialogRef, hideDialog, isDialogVisible, showDialog } = useDialog("center");

  return (
    <div className={styles.rotatingBannerContainer}>
      {isDialogVisible && dialog ? (
        <Dialog {...getDialogProps()}>{renderDialogContent()}</Dialog>
      ) : null}
      {graphic?.imagePath && renderGraphic()}
      <AriaLive hiddenFromScreen={false} ariaLiveSetting="assertive">
        <div className={styles.rotatingBannerContentContainer}>
          {text?.value && renderTextContent(text)}
          {callToAction && <CallToAction {...getCallToActionProps()} />}
        </div>
      </AriaLive>
    </div>
  );

  function renderGraphic() {
    const { altText, display, imagePath } = graphic;

    return display !== "none" ? (
      <img alt={altText} className={styles.rotatingBannerImage} src={imagePath} />
    ) : null;
  }

  function renderTextContent(BannerText) {
    const { value } = BannerText;

    return <HtmlValue htmlValue={value} />;
  }

  function renderDialogContent() {
    const { text: dialogText, title: dialogTitle, callToAction: dialogCallToAction } = dialog || {};

    return (
      <>
        {dialogTitle?.value && <Heading {...getHeadingProps(dialogTitle)} />}
        {dialogText?.value && renderTextContent(dialogText)}
        {callToAction && <CallToAction {...getDialogCallToActionProps(dialogCallToAction)} />}
      </>
    );
  }

  function getCallToActionProps() {
    return {
      ...callToAction,
      ...(dialog && {
        additionalProps: {
          onClick: handleDialogOpen,
        },
      }),
    };
  }

  function getDialogProps() {
    return {
      "aria-label-close": i18n("RotatingBannerPlacement__ARIA_DIALOG_MODAL_CLOSE"),
      "aria-live": i18n("RotatingBannerPlacement__ARIA_DIALOG_MODAL_OPENED"),
      buttonGroupProps: {
        "aria-label-next": i18n("RotatingBannerPlacement__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("RotatingBannerPlacement__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      dialogRef,
      onClose: handleDialogClose,
      revealed: isDialogVisible,
      showClose: true,
    };
  }

  function getDialogCallToActionProps(dialogCallToAction) {
    return {
      ...dialogCallToAction,
      additionalProps: {
        fullWidth: dialogCallToAction.type === "button",
        paddingForFocus: false,
      },
      className: styles.dialogCallToAction,
    };
  }

  function getHeadingProps(dialogHeadingTitle) {
    const { color, fontSize, fontWeight, value } = dialogHeadingTitle;

    return {
      children: <span style={{ color, fontSize, fontWeight }}>{value}</span>,
      className: styles.dialogHeading,
      headingLevel: 3,
    };
  }

  function handleDialogClose() {
    hideDialog();
  }

  function handleDialogOpen() {
    const { analyticsValue } = placementData;

    if (analyticsValue) {
      fireTrack("squid", { page_description: analyticsValue });
    }

    showDialog();
  }
};

RotatingBannerPlacement.displayName = "RotatingBannerPlacement";
RotatingBannerPlacement.propTypes = {
  /** Object that contains placement props, including backgroundColor, callToAction, etc */
  placement: PropTypes.shape({
    /** Object that contains link/button props. */
    callToAction: PropTypes.object,

    /** Object that contains content to show on the dialog. */
    dialog: PropTypes.shape({
      /** Object that contains link/button props. */
      callToAction: PropTypes.object,

      /** Object that contains value and text per screen size. */
      text: PropTypes.shape({
        overrides: PropTypes.shape({
          large: PropTypes.shape({
            value: PropTypes.string,
          }),
          medium: PropTypes.shape({
            value: PropTypes.string,
          }),
          small: PropTypes.shape({
            value: PropTypes.string,
          }),
        }),
        value: PropTypes.string,
      }),

      /** Object that contains title and styles for the heading. */
      title: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string,
      }),
    }),

    /** Object that contains image props. */
    graphic: PropTypes.shape({
      altText: PropTypes.string,
      imagePath: PropTypes.string,
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        medium: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        small: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
      }),
    }),

    /** Object that contains value and override text per screen size. */
    text: PropTypes.shape({
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          value: PropTypes.string,
        }),
        medium: PropTypes.shape({
          value: PropTypes.string,
        }),
        small: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
      value: PropTypes.string,
    }),
  }),
};
